import { mainApi } from '../../../../api/mainApi';
import { AccessState } from '../../../../types';
import { middlewareApi } from '../../../middleware/middlewareApi';
import {
  EventNew,
  EventNewPayload,
  EventNewResponse,
  ImageEventNewsPayload,
} from '../interfaces/event-new.interfaces';

import { setEventNewList } from './eventNewSlice';

export const eventNewTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['eventsNews'],
});
// Define a service using a base URL and expected endpoints
export const eventNewApi = eventNewTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all events
     */
    getEvents: builder.query<
      EventNewResponse,
      {
        profileId: number;
        page: number;
        perPage: number;
        search: string;
        sort: string;
        type_sort: string;
      }
    >({
      queryFn: async ({ profileId, page, search, perPage, sort, type_sort }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<EventNewResponse>('/events', {
            params: {
              user_profile_id: profileId,
              page,
              size: perPage,
              type_sort: type_sort || 'desc',
              ...(sort && { sort }),
              search,
            },
          });

          dispatch(setEventNewList(data?.data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'eventsNews', id } as const)),
              { type: 'eventsNews', id: 'LIST' },
            ]
          : [{ type: 'eventsNews', id: 'LIST' }],
    }),

    /**
     * @GET  event by id
     */
    getEvent: builder.query<EventNew, { profileId?: number; eventId: number }>({
      queryFn: async ({ profileId, eventId }, { dispatch, getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = currentProfile?.profileId;
          const { data } = await mainApi.get<EventNew>(`/events/${eventId}`, {
            params: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'eventsNews', profileId }],
    }),

    /**
     * @POST create event
     */
    addEvent: builder.mutation<
      EventNew,
      { profileId: number; eventNewPayload: Partial<EventNewPayload> }
    >({
      queryFn: async ({ profileId, eventNewPayload }) => {
        try {
          const { data } = await mainApi.post<EventNew>('/events', {
            user_profile_id: profileId,
            ...eventNewPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'eventsNews', id: 'LIST' }],
    }),

    /**
     * @PUT update event
     */
    updateEvent: builder.mutation<
      EventNew,
      { profileId: number; eventId: number; eventNewPayload: Partial<EventNewPayload> }
    >({
      queryFn: async ({ profileId, eventId, eventNewPayload }) => {
        try {
          const { data } = await mainApi.put<EventNew>(`/events/${eventId}`, {
            user_profile_id: profileId,
            ...eventNewPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'eventsNews', id: 'LIST' }],
    }),

    /**
     * @DELETE remove event
     */
    deleteEvent: builder.mutation<EventNew, { profileId: number; eventId: number }>({
      queryFn: async ({ profileId, eventId }) => {
        try {
          const { data } = await mainApi.delete(`/events/${eventId}`, {
            data: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'eventsNews', id: 'LIST' }],
    }),

    /**
     * @POST add image event new
     */
    addImageEventNews: builder.mutation<EventNew, { imagePayload: ImageEventNewsPayload }>({
      queryFn: async ({ imagePayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = currentProfile?.profileId;
          const form = new FormData();
          form.append('files[]', imagePayload.image);
          form.append('type_document', '5');
          const { data } = await mainApi.post(`/events/${imagePayload.eventId}/photo`, form, {
            params: {
              user_profile_id,
            },
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'eventsNews', id: 'LIST' }],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useAddEventMutation,
  useDeleteEventMutation,
  useUpdateEventMutation,
  useAddImageEventNewsMutation,
  useGetEventsQuery,
  useLazyGetEventQuery,
} = eventNewApi;
